import React, { useState } from 'react';
import { observer } from 'mobx-react';
import pricingStore from '../../Stores/PricingStore';
import './PricingCards.scss';

const PricingCards = observer(({ 
  onPlanSelect, 
  selectedPlan = '',
  showPaymentElement = false,
  estimateAudioDuration = (chars) => Math.round((chars / 10000) * 10.5),
  renderCta = null
}) => {
  const [isYearly, setIsYearly] = useState(false);

  const renderToggle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', margin: '20px 0' }}>
      <div 
        onClick={() => setIsYearly(false)}
        style={{
          padding: '8px 20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '20px',
          cursor: 'pointer',
          backgroundColor: !isYearly ? 'rgba(255,255,255,0.1)' : 'transparent',
          transition: 'background-color 0.2s ease'
        }}
      >
        Monthly
      </div>

      <div
        onClick={() => setIsYearly(true)}
        style={{
          padding: '8px 20px', 
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '20px',
          cursor: 'pointer',
          backgroundColor: isYearly ? 'rgba(255,255,255,0.1)' : 'transparent',
          transition: 'background-color 0.2s ease'
        }}
      >
        Yearly <span style={{ color: '#4CAF50' }}>(Save more than 40%)</span>
      </div>
    </div>
  );

  const renderProOnlyCards = () => (
    <>
      {/* Monthly Pro Plan Card */}
      <div
        onClick={() => onPlanSelect(pricingStore.proMonthlySlug)}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan === pricingStore.proMonthlySlug ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Monthly Pro</div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>${pricingStore.proMonthlyPrice}</div>
        <div style={{ opacity: 0.8 }}>per month</div>
        <div style={{ fontSize: '14px', opacity: 0.7, marginTop: '5px' }}>No contract, cancel anytime</div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
          <li>✨ All Premium Features</li>
          <li>🎙️ Premium voices</li>
          <li>🌍 Multi-language support</li>
          <li>🤖 AI Summaries</li>
          <li>🔗 Add Feeds by URL</li>
          <li>💾 Download articles as MP3</li>
          <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>

      {/* Annual Pro Plan Card */}
      <div
        onClick={() => onPlanSelect(pricingStore.proYearlySlug)}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan === pricingStore.proYearlySlug ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box',
          position: 'relative'
        }}
      >
        <div style={{ 
          position: 'absolute',
          top: '-12px',
          right: '20px',
          backgroundColor: 'rgb(255 143 65)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          BEST VALUE
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Annual Pro</div>
        <div style={{ marginBottom: '5px' }}>
          <span style={{ 
            fontSize: '16px', 
            textDecoration: 'line-through', 
            opacity: 0.7,
            marginRight: '8px'
          }}>
            ${pricingStore.proMonthlyPrice * 12}
          </span>
          <span style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            color: 'rgb(255 143 65)'
          }}>
            ${pricingStore.proYearlyPrice}
          </span>
        </div>
        <div style={{ opacity: 0.8 }}>per year</div>
        <div style={{ 
          fontSize: '14px', 
          color: '#4CAF50', 
          fontWeight: 'bold',
          marginTop: '5px'
        }}>
          Save ${(pricingStore.proMonthlyPrice * 12 - pricingStore.proYearlyPrice).toFixed(2)} ({Math.round(100 - (pricingStore.proYearlyPrice / (pricingStore.proMonthlyPrice * 12)) * 100)}% off)
        </div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
          <li>✨ All Premium Features</li>
          <li>🎙️ Premium voices</li>
          <li>🌍 Multi-language support</li>
          <li>🤖 AI Summaries</li>
          <li>🔗 Add Feeds by URL</li>
          <li>💾 Download articles as MP3</li>
          <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>
    </>
  );

  const renderAllPlansCards = () => (
    <>
      {/* Basic Plan Card */}
      <div
        onClick={() => onPlanSelect(isYearly ? 'basic-yearly' : 'basic-monthly')}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan.includes('basic') ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Audioread Basic</div>
        <div>
          {isYearly ? (
            <>
              <div style={{ marginBottom: '5px' }}>
                <span style={{ 
                  fontSize: '16px', 
                  textDecoration: 'line-through', 
                  opacity: 0.7,
                  marginRight: '8px'
                }}>
                  ${(pricingStore.basicMonthlyPrice * 12).toFixed(2)}
                </span>
                <span style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: 'rgb(255 143 65)'
                }}>
                  ${pricingStore.basicYearlyPrice}
                </span>
              </div>
              <div style={{ opacity: 0.8 }}>per year</div>
              <div style={{ 
                fontSize: '14px', 
                color: '#4CAF50', 
                fontWeight: 'bold',
                marginTop: '5px'
              }}>
                Save ${(pricingStore.basicMonthlyPrice * 12 - pricingStore.basicYearlyPrice).toFixed(2)} (${Math.round((1 - pricingStore.basicYearlyPrice / (pricingStore.basicMonthlyPrice * 12)) * 100)}% off)
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                ${pricingStore.basicMonthlyPrice}
              </div>
              <div style={{ opacity: 0.8 }}>per month</div>
            </>
          )}
        </div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
          <li>✨ Remove all ads</li>
          <li>🎧 Listen on your podcast player</li>
          <li>📚 Add urls, pdfs, emails, feeds+</li>
          <li>🗣️ Life-like voices</li>
          <li>♾️ Up to ~{estimateAudioDuration(1000000)} minutes of articles</li>
          <li>📱 Unlimited Feeds</li>
          <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>

      {/* Pro Plan Card */}
      <div
        onClick={() => onPlanSelect(isYearly ? pricingStore.proYearlySlug : pricingStore.proMonthlySlug)}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan.includes('pro') ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box',
          position: 'relative'
        }}
      >
        <div style={{ 
          position: 'absolute',
          top: '-12px',
          right: '20px',
          backgroundColor: 'rgb(255 143 65)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {pricingStore.isHighIncome ? 'PREMIUM' : 'MOST POPULAR'}
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>
          {pricingStore.isHighIncome ? 'Premium Pro' : 'Audioread Pro'}
        </div>
        <div>
          {isYearly ? (
            <>
              <div style={{ marginBottom: '5px' }}>
                <span style={{ 
                  fontSize: '16px', 
                  textDecoration: 'line-through', 
                  opacity: 0.7,
                  marginRight: '8px'
                }}>
                  ${(pricingStore.proMonthlyPrice * 12).toFixed(2)}
                </span>
                <span style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: 'rgb(255 143 65)'
                }}>
                  ${pricingStore.proYearlyPrice}
                </span>
              </div>
              <div style={{ opacity: 0.8 }}>per year</div>
              <div style={{ 
                fontSize: '14px', 
                color: '#4CAF50', 
                fontWeight: 'bold',
                marginTop: '5px'
              }}>
                Save ${(pricingStore.proMonthlyPrice * 12 - pricingStore.proYearlyPrice).toFixed(2)} (${Math.round((1 - pricingStore.proYearlyPrice / (pricingStore.proMonthlyPrice * 12)) * 100)}% off)
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                ${pricingStore.proMonthlyPrice}
              </div>
              <div style={{ opacity: 0.8 }}>per month</div>
            </>
          )}
        </div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
          <li>✨ Everything in Basic</li>
          <li>🎙️ Premium voices</li>
          <li>🌍 Multi-language support</li>
          <li>🤖 AI Summaries</li>
          <li>🔗 Add Feeds by URL</li>
          <li>💾 Download articles as MP3</li>
          <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '100vw', width: '100%', justifyContent: 'center', padding: '0 20px' }}>
      <div
        className="pricing-card"
        style={{
          marginTop: '15px',
          width: '100%',
          maxWidth: '800px',
          border: "2px solid hsla(0,0%,100%,.1)",
          padding: '20px',
          minHeight: 'fit-content',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {!pricingStore.showOnlyPro && !pricingStore.isHighIncome && renderToggle()}
        
        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '20px', 
          margin: '20px 0',
          width: '100%',
          maxWidth: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          opacity: showPaymentElement ? 0.5 : 1,
          pointerEvents: showPaymentElement ? 'none' : 'auto',
          flexWrap: 'wrap',
          boxSizing: 'border-box',
          padding: '0 20px'
        }}>
          {pricingStore.isHighIncome ? renderProOnlyCards() : (pricingStore.showOnlyPro ? renderProOnlyCards() : renderAllPlansCards())}
        </div>

        {renderCta && (
          <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
            {renderCta('pricing')}
          </div>
        )}
      </div>
    </div>
  );
});

export default PricingCards; 